import { Error } from '@mui/icons-material';
import { Alert, Button, Tab, Tabs, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTypedParams } from 'react-router-typesafe-routes/dom';
import { FragranceId } from '~/api/api.declaration';
import { AppRoutes, useNavBack } from '~/app/app.routes';
import { AuthService } from '~/service/service.auth';
import { BrandService } from '~/service/service.brand';
import { FragranceService } from '~/service/service.fragrance';
import { IngredientService } from '~/service/service.ingredient';
import { NoteService } from '~/service/service.note';
import { OlfactiveService } from '~/service/service.olfactive';
import { PerfumerService } from '~/service/service.perfumer';
import { UIService } from '~/service/service.ui';
import { FragranceCreateDto, FragranceLinkDto, FragranceOfferDto } from '~/shared/dto/admin/fragrance.dto';
import { Form } from '~/shared/form/form';
import { useService } from '~/shared/service/service.base';
import { FragranceExperiencesDialog } from '~/view/dialog/dialog.fragrance.experiences';
import { FragranceModerationDialog } from '~/view/dialog/dialog.fragrance.moderation';
import { FeedbackDrawer } from '~/view/feedback/feedback.drawer';
import { EditCenter, EditContainer, EditRight } from '~/view/view.edit';
import { FragranceAffiliate } from './fragrance.edit.affiliate';
import { FragranceClasped } from './fragrance.edit.clasped';
import { FragranceGeneral } from './fragrance.edit.general';
import { FragranceImpressions } from './fragrance.edit.impressions';
import { FragranceLinks } from './fragrance.edit.links';
import { FragranceNose } from './fragrance.edit.nose';
import { FragranceOffers } from './fragrance.edit.offers';
import { FragrancePhotos } from './fragrance.edit.photos';
import { FragranceSuggestions } from './fragrance.suggestions';

type FragranceEditTab =
	| 'general'
	| 'nose'
	| 'impressions'
	| 'clasped'
	| 'links'
	| 'offers'
	| 'affiliate'
	| 'suggestions';

type FragranceEditWrapperProps = PropsWithChildren & {
	tab: FragranceEditTab;
	form: Form<FragranceCreateDto>;
};

const FragranceEditWrapper: FC<FragranceEditWrapperProps> = observer(({ children, tab, form }) => {
	const { id } = useTypedParams(AppRoutes.FragranceEdit);
	const [fragrances, brands, olfactives, perfumers, notes, ingredients] = useService(
		FragranceService,
		BrandService,
		OlfactiveService,
		PerfumerService,
		NoteService,
		IngredientService,
	);

	useEffect(() => brands.fetchNames(), [brands]);
	useEffect(() => olfactives.fetchNames(), [olfactives]);
	useEffect(() => perfumers.fetchNames(), [perfumers]);
	useEffect(() => ingredients.fetchNames(), [ingredients]);
	useEffect(() => notes.fetchNames(), [notes]);

	return (
		<>
			<Form.Provider value={form}>{children}</Form.Provider>
			{id && <FragranceModerationDialog />}
			{id && <FragranceExperiencesDialog fragranceId={id as FragranceId} />}
			{tab === 'suggestions' && <FeedbackDrawer model={fragrances.suggestions.drawer} />}
		</>
	);
});

export const FragranceEditPage = observer(() => {
	const { id } = useTypedParams(AppRoutes.FragranceEdit);
	const [ui, auth, fragrances] = useService(UIService, AuthService, FragranceService);
	const navBack = useNavBack();

	const { moderation } = fragrances;
	const [tab, setTab] = useState<FragranceEditTab>('general');

	const { t: trValidation } = useTranslation('validation');

	const form = Form.use(FragranceCreateDto, {
		trValidation,
		name: 'FragranceCreateDto',
		onSubmit: async dto => {
			await fragrances.save(id, dto).then(() => {
				ui.notify('Fragrance saved successfully', 'success');
			});
		},
		initial: async () => {
			const f = await fragrances.getOne(id);
			moderation.setData(f?.moderation);
			return f;
		},
	});

	useEffect(() => fragrances.setSuperId(form.initial?.superId), [form.initial?.superId, fragrances]);

	ui.usePageTitle(form.initial?.name ? `Fragrance - ${form.initial?.name}` : '');
	const canSave = id
		? auth.can('fragrance', 'UPDATE') && moderation.data?.status !== 'ARCHIVED'
		: auth.can('fragrance', 'CREATE');

	const reload = useCallback(() => {
		fragrances.getOne(id).then(f => f && (form.apply(f), moderation.setData(f.moderation)));
	}, [form, fragrances, id, moderation]);

	const fillNoseNotes = useCallback(() => {
		const set = new Set([
			...(form.$noseNotes.value ?? []),
			...(form.$brandTopNotes.value ?? []),
			...(form.$brandMidNotes.value ?? []),
			...(form.$brandBaseNotes.value ?? []),
		]);
		form.$noseNotes.set([...set]);
	}, [form.$brandBaseNotes.value, form.$brandMidNotes.value, form.$brandTopNotes.value, form.$noseNotes]);

	return (
		<FragranceEditWrapper tab={tab} form={form}>
			<EditContainer async={fragrances.async}>
				<FragrancePhotos form={form} id={id} reload={reload} />
				<EditCenter>
					<Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="main tabs" centered>
						<Tab
							label="General"
							value="general"
							iconPosition="end"
							icon={<TabIcon value="general" form={form} />}
						/>
						<Tab label="Nose info" value="nose" />
						<Tab label="Impressions" value="impressions" />
						<Tab label="Clasped" value="clasped" />
						<Tab
							label="Links"
							value="links"
							iconPosition="end"
							icon={<TabIcon value="links" form={form} />}
						/>
						<Tab
							label="Offers"
							value="offers"
							iconPosition="end"
							icon={<TabIcon value="offers" form={form} />}
						/>
						<Tab label="Affiliate" value="affiliate" />
						<Tab label="Suggestions" value="suggestions" />
					</Tabs>

					{tab === 'general' && <FragranceGeneral id={id as FragranceId} form={form} />}
					{tab === 'nose' && <FragranceNose form={form} />}
					{tab === 'impressions' && <FragranceImpressions id={id as FragranceId} form={form} />}
					{tab === 'clasped' && <FragranceClasped form={form} />}
					{tab === 'affiliate' && <FragranceAffiliate form={form} reload={reload} />}
					{tab === 'offers' && <FragranceOffers field={form.$offers} />}
					{tab === 'links' && <FragranceLinks field={form.$links} />}
					{tab === 'suggestions' && <FragranceSuggestions fragranceId={id as FragranceId} />}
				</EditCenter>
				<EditRight>
					{form.dirty && (
						<Alert variant="filled" severity="warning" icon={null}>
							There are unsaved changes
						</Alert>
					)}
					<Button
						variant="contained"
						onClick={() => form.submit()?.then(() => navBack())}
						disabled={!canSave}
					>
						Save and back
					</Button>
					<Button variant="outlined" onClick={() => form.submit()?.then(reload)} disabled={!canSave}>
						Save
					</Button>
					<Button variant="outlined" onClick={navBack}>
						Cancel
					</Button>
					<br />
					<Button variant="outlined" onClick={form.reset}>
						Reset
					</Button>
					<Button variant="outlined" onClick={fillNoseNotes}>
						Fill Nose notes
					</Button>

					{id && (
						<>
							<br />
							<Tooltip title={moderation?.data?.status}>
								<Button
									variant="outlined"
									color={
										moderation?.data?.status === 'PENDING'
											? 'warning'
											: moderation?.data?.status === 'REJECTED'
												? 'error'
												: 'primary'
									}
									onClick={() => moderation.showDialog(id as FragranceId, reload)}
								>
									Moderation
								</Button>
							</Tooltip>
							<br />
							<Button variant="outlined" onClick={() => ui.openHistory('FRAGRANCE', id)}>
								Show history
							</Button>
							<Button
								variant="outlined"
								onClick={() => window.open(AppRoutes.BrandEdit.buildPath({ id: +form.$brandId.value }))}
							>
								Show brand
							</Button>
							{auth.can('user', 'READ') && (
								<Button variant="outlined" onClick={() => ui.showDialog(FragranceExperiencesDialog)}>
									Show experiences
								</Button>
							)}
							<br />
							<Button
								disabled={!auth.can('fragrance', 'DELETE')}
								variant="outlined"
								color="error"
								onClick={() =>
									ui.confirmDelete(() =>
										fragrances.delete(id).then(() => {
											navBack();
											ui.notify('Fragrance deleted successfully', 'success');
										}),
									)
								}
							>
								Delete
							</Button>
						</>
					)}
				</EditRight>
			</EditContainer>
		</FragranceEditWrapper>
	);
});

const TabIconError = <Error color="error" fontSize="small" sx={{ ml: 0.5 }} />;
const TabIcon = observer<{ value: FragranceEditTab; form: Form<FragranceCreateDto> }>(({ value, form }) => {
	switch (value) {
		case 'general':
			if (form.errors?.some(e => !e.children?.length && e.target instanceof FragranceCreateDto))
				return TabIconError;
			return;
		case 'links':
			if (
				form.errors?.some(
					e =>
						e.children &&
						e.children.some(c => Array.isArray(c.target) && c.target[0] instanceof FragranceLinkDto),
				)
			)
				return TabIconError;
			return;
		case 'offers':
			if (
				form.errors?.some(
					e =>
						e.children &&
						e.children.some(c => Array.isArray(c.target) && c.target[0] instanceof FragranceOfferDto),
				)
			)
				return TabIconError;
			return;
		default:
			return;
	}
});
